import { Injectable, inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from '@/core/services/api/api.service';
import {
  CreditActionPayload,
  CreditSaveComite,
  CreditSaveDecisionFinale,
  CreditSaveTirage,
  CreditTirageSearch,
} from '../../interfaces/credit.interface';

@Injectable({ providedIn: 'root' })
export class CreditWorkflowService {
  private readonly api = inject(ApiService);
  private readonly endpoint = '/credit';

  // ── Observations / Soumission ─────────────────────────────────────────────
  // Remplacer l'enpoint actuelle par : /workflowCrd
  saveCrdObservation(data: CreditActionPayload, creditSpecial?: number) {
    const endpoint =
      // creditSpecial === 1 ? '/workflowCrd' :
      '/workflowCrd';
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + endpoint, data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  pauseDemande(data: CreditActionPayload) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/pauseDemande', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  playDemande(data: CreditActionPayload) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/playDemande', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  rappelDossierValide(data: CreditActionPayload) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/rappelDossierValide', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Legacy parity : POST /remettreDansLeSystem/{refDemande} (motif = observation, decision = 2). */
  remettreEnCircuit(data: {
    refDemande: string;
    observation: string;
    motif: string;
    decision: number;
    password: string;
  }) {
    return this.api
      .post<{
        status: number;
        message?: string;
      }>(this.endpoint + '/remettreDansLeSystem/' + data.refDemande, data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  clotureDemande(data: CreditActionPayload) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/clotureDemande', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  checkActeOrVisite(data: CreditActionPayload) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/check_acte_or_visite', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Tirage découvert ──────────────────────────────────────────────────────
  searchTirage(numDmde: string) {
    return this.api
      .post<CreditTirageSearch>(this.endpoint + '/searchDmde', { numDmde })
      .pipe(catchError((err) => throwError(() => err)));
  }

  saveTirage(data: CreditSaveTirage) {
    return this.api
      .post<{
        status: number;
        demande: { refDemande: string };
        message?: string;
      }>(this.endpoint + '/saveCrdTirage', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Comité / Décision ─────────────────────────────────────────────────────
  saveComiteDecision(data: CreditSaveComite) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveComite', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Legacy parity: /savePrecomite */
  savePreComiteDecision(data: CreditSaveComite) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/savePrecomite', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  saveDecisionFinale(data: CreditSaveDecisionFinale) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveCrDecision', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  submitAnalyse(data: { refDemande: string; observation: string; password: string }) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/submitAnalyse', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Propositions & Evaluations ────────────────────────────────────────────
  savePropositionAR(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveProposition', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Legacy parity: /saveContreEvaluation */
  saveContreEvaluation(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveContreEvaluation', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Legacy parity: /getTaxeCrd */
  calculateFraisDossier(data: Record<string, unknown>) {
    return this.api
      .post<Record<string, unknown>>(this.endpoint + '/getTaxeCrd', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  saveSWOT(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveAswot', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Décaissement ──────────────────────────────────────────────────────────
  annulerDecaissement(data: CreditActionPayload) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/annulerPrDesistement', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  workByDerogation(data: CreditActionPayload) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/workflowCrd', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  saveAvsf(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveAvsf', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  modifAvsf(id: number, data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + `/avsFond/${id}`, data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  saveDecaissement(data: { refDemande: string; numPret: string; numContrat: string }) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveDecaissement', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  updateMontantEmprunte(data: Record<string, unknown>) {
    return this.api
      .post<{
        status: number;
        message?: string;
      }>(this.endpoint + '/mis_a_jour_montant_emprunter', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  envoiAvanceFond(data: CreditActionPayload) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/validation_avs_fond', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  ajournAvsf(data: CreditActionPayload) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/ajourne_avs_fond', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  ajouterNumPerfectAvsf(id: number, data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + `/avsFond/${id}`, data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Affectation ───────────────────────────────────────────────────────────
  affecterDemandeAR(
    data: {
      refDemande: string;
      decision: number;
      zone: number;
      codeAr: string;
      password: string;
      observation?: string;
    },
    creditSpecial?: number,
  ) {
    const endpoint =
      // creditSpecial === 1 ? '/workflowCrd' :
      '/workflowCrd';
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + endpoint, data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deplacerUnDossier(data: { refDemande: string; statut: number; observation: string }) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/deplacerUnDossier', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  affecterSuperviseurPME(
    data: {
      refDemande: string;
      decision: number;
      supOpSen: number;
      password: string;
      observation?: string;
    },
    creditSpecial?: number,
  ) {
    const endpoint =
      // creditSpecial === 1 ? '/workflowCrd' :
      '/workflowCrd';
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + endpoint, data)
      .pipe(catchError((err) => throwError(() => err)));
  }
}
